// eslint-disable-next-line no-unused-vars
import React, { useState } from 'react';
import { func, object, string } from 'prop-types';
import { useTranslation } from 'react-i18next';

import CreateWishlistContent from 'Components/Wishlists/AddToModal/CreateWishlistContent';
import useAnalytics from 'Hooks/useAnalytics';
import useWishlists from 'Hooks/useWishlists';
import useToastNotifications from 'Hooks/useToastNotifications';

const CreateWishlist = ({
  product,
  sourceWishlist,
  action,
  onComplete,
  onCancel
}) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const toast = useToastNotifications();
  const { createList, updateProduct, data } = useWishlists();
  const [name, setName] = useState('');
  const [isDefault, setDefault] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleNameChange = event => {
    const nextName = event.target.value;
    const duplicate = Object.values(data || {}).some(
      list => list.name.trim().toLowerCase() === nextName.trim().toLowerCase()
    );
    setName(nextName);
    setError(duplicate ? t('wishlists.errors.sameName') : null);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const form = event.target.closest('form');
    if (!form.reportValidity() || error) {
      return;
    }

    setLoading(true);
    analytics.track('add_to_destination_modal_create_wishlist_submitted', {
      product_id: product?.productId || product?.id,
      action: action || 'add'
    });
    try {
      const list = await createList({
        name: name.trim(),
        isDefault
      });
      if (!list) {
        throw new Error(t('wishlists.errors.doesNotExist'));
      }
      await updateProduct(list.id, {
        id: product.id,
        quantity: product.quantity || product.minQuantity || 1
      });
      if (action === 'move' && sourceWishlist?.id) {
        await updateProduct(sourceWishlist.id, {
          id: product.id,
          quantity: 0
        });
      }
      toast.addNotification({
        type: 'success',
        title: t('wishlists.successfullyAddedToWishlist')
      });
      analytics.track('add_to_destination_modal_wishlist_created', {
        wishlist_id: list.id,
        product_id: product?.productId || product?.id,
        is_default: isDefault
      });
      onComplete();
    } catch (e) {
      analytics.track('add_to_destination_modal_create_wishlist_failed', {
        product_id: product?.productId || product?.id
      });
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CreateWishlistContent
      product={ product }
      name={ name }
      isDefault={ isDefault }
      error={ error }
      loading={ loading }
      onNameChange={ handleNameChange }
      onSetDefault={ checked => {
        analytics.track('add_to_destination_modal_set_default_wishlist_changed', { checked });
        setDefault(checked);
      } }
      onSubmit={ handleSubmit }
      onCancel={ onCancel }
    />
  );
};

CreateWishlist.propTypes = {
  product: object,
  sourceWishlist: object,
  action: string,
  onComplete: func,
  onCancel: func
};

export default CreateWishlist;
